







































import Vue from 'vue';
import {Component, Prop} from "vue-property-decorator";
import {Icon} from "@/helpers/icons";
import AsiPageTitle from "@/components/common/AsiPageTitle.vue";
import {SnackbarMessage} from "@/models/SnackbarMessage";
import AsiConfirmDialog from "@/components/common/AsiConfirmDialog.vue";
import BillModel from "@/models/bill/Bill";
import {isEmpty} from "@/helpers/functions_asi";
import AsiTabs from "@/components/common/AsiTabs";
import BillTabDetails from "@/components/bill/BillTabDetails.vue";
import {BillState, Permissions} from "@/helpers/constants";
import AsiMenuItem from "@/components/common/AsiMenuItem.vue";
import BillTabReports from "@/components/bill/BillTabReports.vue";
import StateBar from "@/components/StateBar.vue";
import AddressBilling from "@/models/address/AddressBilling";
import Company from "@/models/company/Company";

@Component({
	components: {StateBar, BillTabReports, AsiMenuItem, BillTabDetails, AsiTabs, AsiConfirmDialog, AsiPageTitle}
})
export default class Bill extends Vue {

	@Prop({type: String, required: true})
	public id!: string;

	private icons = Icon;
	private bill: BillModel | null = null;

	private activeTab: number = 0;
	private loading: boolean = false;
	private loadingCompany: boolean = false;
	private loadingSelection: boolean = false;
	private company: Company | null = null;
	private hasEnoughBillingInformation: boolean = true;

	private get title(): string {
		if (this.bill === null) return '';
		return isEmpty(this.bill.name) || this.bill.name === null ? this.bill.number.toString() : this.bill.name;
	}

	private get billName(): string {
		if (this.bill === null) return '';
		return this.bill.name ?? this.bill.number.toString();
	}

	private get companyName(): string {
		if (this.bill === null) return '';
		return this.bill.company.name;
	}

	private get isBillMailed(): boolean {
		return this.company?.isBillMailed ?? false;
	}

	private get isStateLocked(): boolean {
		if (this.bill === null) return true;
		return this.bill.state === BillState.locked;
	}

	private get isAdmin(): boolean {
		return this.$store.getters['user/isAdmin'];
	}

	private get isBilling(): boolean {
		return this.$store.getters['user/hasPermission'](Permissions.rightBilling);
	}

	private get isCustomer(): boolean {
		return this.$store.getters['user/hasPermission'](Permissions.rightCustomer);
	}

	private get isDeletable(): boolean {
		return this.bill !== null && this.bill.state !== BillState.locked;
	}

	public mounted(): void {
		this.loadBill();
	}

	private loadBill(): void {
		this.loading = true;
		this.$billService.bill(this.id)
			.then(bill => {
				this.bill = bill;
			})
			.catch(() => {
				this.$store.commit('ui/showSnackbar', {
					message: this.$t('snackbar.loadingError'),
					color: 'error',
				} as SnackbarMessage);
			})
			.finally(() => {
				this.loading = false;
				this.loadCompany();
			});
	}

	private deleteBill(): void {
		const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
		confirm.openDialog().then((res: boolean) => {
			if (!res || this.bill === null) return;

			this.loading = true;
			this.$billService.delete(this.bill.id)
				.then(() => this.$router.push({name: 'Bills'}))
				.catch(() => {
					this.$store.commit('ui/showSnackbar', {
						message: this.$t('snackbar.deleteError'),
						color: 'error',
					} as SnackbarMessage);
				})
				.finally(() => this.loading = false);
		});
	}

	private setPaid(isPaid: boolean): void {
		const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
		confirm.openDialog().then((res: boolean) => {
			if (!res || this.bill === null) return;

			this.loading = true;
			this.$billService.setPaid(this.bill.id, isPaid)
				.then(() => this.loadBill())
				.catch(this.updateError)
				.finally(() => this.loading = false);
		});
	}

	private loadCompany(): void {
		if (this.bill === null) return;

		this.loadingCompany = true;
		this.$companyService.company(this.bill.company.id)
			.then(company => {
				this.company = company;
				this.hasEnoughBillingInformation = AddressBilling.hasEnoughInformationForBilling(company.billingAddress);
			})
			.catch(() => {
				this.$store.commit('ui/showSnackbar', {
					message: this.$t('snackbar.loadingError'),
					color: 'error',
				} as SnackbarMessage);
			})
			.finally(() => this.loadingCompany = false);
	}

	private unlockBill(): void {
		this.setBillState(BillState.draft);
	}

	private lockBill(): void {
		this.setBillState(BillState.locked);
	}

	private setBillState(state: BillState): void {
		const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
		confirm.openDialog().then((res: boolean) => {
			if (!res || this.bill === null) return;

			this.loading = true;
			this.$billService.state(this.bill.id, state)
				.then(this.loadBill)
				.catch(this.updateError)
				.finally(() => this.loading = false);
		});
	}

	private downloadPreview(): void {
		if (this.bill === null) return;
		this.loading = true;
		this.$billService.downloadPreview(this.bill.id)
			.then(res => {
				const fileURL = window.URL.createObjectURL(new Blob([res.data]));
				const fileLink = document.createElement('a');
				const contentDisposition = res.headers['content-disposition'].split(';').map((x: string) => x.trim());

				const filename = contentDisposition.find((dispositionEntry: string) => {
					const dispositionEntrySplit = dispositionEntry.trim().split('=');
					if (dispositionEntrySplit.length > 1) {
						if ('filename' === dispositionEntrySplit[0]) {
							return true;
						}
					}
				}).split('=')[1];
				fileLink.href = fileURL;
				fileLink.setAttribute('download', filename);
				document.body.appendChild(fileLink);
				fileLink.click();
				fileLink.remove();
			})
			.catch(() => {
				this.$store.commit('ui/showSnackbar', {
					message: this.$t('snackbar.previewBillError'),
					color: 'error',
				} as SnackbarMessage);
			})
			.finally(() => this.loading = false);
	}

	private sendOrPrint(): void {
		if (this.bill === null || this.bill.state !== BillState.locked) return;

		const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
		confirm.openDialog().then((res: boolean) => {
			if (!res || this.bill === null) return;

			this.$billService.sendOrPrint(this.bill.id)
				.then(res => {
					if (parseInt(res.headers['content-length']) === 0) {
						this.$store.commit('ui/showSnackbar', {
							message: this.$t('snackbar.emailSentSuccessfully'),
							color: 'success',
						} as SnackbarMessage);
						return;
					}

					const fileURL = window.URL.createObjectURL(new Blob([res.data]));
					const fileLink = document.createElement('a');
					const contentDisposition = res.headers['content-disposition'].split(';').map((x: string) => x.trim());

					const filename = contentDisposition.find((dispositionEntry: string) => {
						const dispositionEntrySplit = dispositionEntry.trim().split('=');
						if (dispositionEntrySplit.length > 1) {
							if ('filename' === dispositionEntrySplit[0]) {
								return true;
							}
						}
					}).split('=')[1];
					fileLink.href = fileURL;
					fileLink.setAttribute('download', filename);
					document.body.appendChild(fileLink);
					fileLink.click();
					fileLink.remove();

					this.loadBill();
				})
				.catch(() => {
					this.$store.commit('ui/showSnackbar', {
						message: this.$t('snackbar.sendOrPrintBillError'),
						color: 'error',
					} as SnackbarMessage);
				})
				.finally(() => this.loading = false);
		});
	}

	private updateError(): void {
		this.$store.commit('ui/showSnackbar', {
			message: this.$t('snackbar.updateError'),
			color: 'error',
		} as SnackbarMessage);
	}

}
